import { ReceiveShipmentsService } from '@/api';
import type { ProcessStatus } from '@/api/process';
import { ProcessesService } from '@/api/process';
import type {
  CloseDeliveryRequest,
  DeliveryDetailsRow,
  GetDeliveryDetailsResponse,
  PdmFailedDelivery,
} from '@/api/receive';
import {
  ClosePdmDeliveryResponse200,
  ClosePdmDeliveryResponse207,
  PdmService,
} from '@/api/receive';
import { RootState } from '@/app/rootReducer';
import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { ReactComponent as DeliveryIcon } from '@/assets/icons/delivery.svg';
import { CTAContainer } from '@/components/layout/CTAContainer';
import { ModalAttention } from '@/components/layout/ModalAttention';
import { ModalDataSavedError } from '@/components/layout/ModalDataSaved';

import { UIBox } from '@/components/ui/Box';
import { UIButton } from '@/components/ui/Button';
import { UICheckbox } from '@/components/ui/Checkbox';
import { ErrorSnackbar } from '@/components/ui/ErrorSnackbar';
import { PageLoader } from '@/components/ui/PageLoader';
import { Typography } from '@/components/ui/Typography';

// import {
//   changeEpcStatus as MNDLChangeEpcStatus,
//   checkEpcs as MNDLCheckEpcs,
//   fetchDeliveryDetails as MNDLFetchDeliveryDetails,
//   initCheckedEpcs as MNDLInitCheckedEpcs,
//   initManageDeliveryState as MNDLInitManageDeliveryState,
//   setDeliveryStatuses as MNDLSetDeliveryStatuses,
//   setManagedDeliveriesInUse as MNDLSetManagedDeliveriesInUse,
//   setManagedDeliveryInUse as MNDLSetManagedDeliveryInUse,
//   setSearchedDeliveryCode as MNDLSetSearchedDeliveryCode,
//   setSearchedDeliveryCodes as MNDLSetSearchedDeliveryCodes,
//   setSearchedStoreCode as MNDLSetSearchedStoreCode,
//   setSearchedStoreCodes as MNDLSetSearchedStoreCodes,
//   setShouldFetchDeliveries as MNDLSetShouldFetchDeliveries,
// } from '@/features/deliveriesManage/deliveriesManageSlice';
import {
  changeEpcStatus as PDMChangeEpcStatus,
  checkEpcs as PDMCheckEpcs,
  fetchDeliveryDetails as PDMFetchDeliveryDetails,
  initCheckedEpcs as PDMInitCheckedEpcs,
  initManageDeliveryState as PDMInitManageDeliveryState,
  setDeliveryStatuses as PDMSetDeliveryStatuses,
  setManagedDeliveriesInUse as PDMSetManagedDeliveriesInUse,
  setManagedDeliveryInUse as PDMSetManagedDeliveryInUse,
  setSearchedDeliveryCode as PDMSetSearchedDeliveryCode,
  setSearchedDeliveryCodes as PDMSetSearchedDeliveryCodes,
  setSearchedStoreCode as PDMSetSearchedStoreCode,
  setSearchedStoreCodes as PDMSetSearchedStoreCodes,
  setShouldFetchDeliveries as PDMSetShouldFetchDeliveries,
} from '@/features/deliveriesManage/partialDeliveriesManageSlice';
import { useSelector } from '@/hooks/useSelector';
import PdmResultModal from '@/pages/ManageDelivery/PageManageDeliveryDetails_Partial/ClosedDeliveriesModal';
import DeliveryList from '@/pages/ManageDelivery/PageManageDeliveryDetails_Partial/DeliveryList/DeliveryList';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';

import { palette } from '@/theme';
import { EPC_STATUS, EpcStatusValues } from '@/types/enum';
import { Accordion } from '@material-ui/core';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RoomIcon from '@material-ui/icons/Room';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router';
import { useAsync } from 'react-use';

import {
  StyledSelect,
  StyledStickyHeader,
  StyledTablesHeader,
  StyledTableWrapper,
} from './style';
import type { EpcStatus, Params, SelectValues } from './types';

type ManageDeliveryFlowType = /*'MNDL' |*/ 'PDM';

const manageDeliveryConfigByFlow = {
  // MNDL: {
  //   selectState: (state: RootState) => state.manageDeliveries,
  //   flowTypeRequest: undefined,
  //   status: ReceivingStatus.NEW,
  //   setDeliveryStatuses: MNDLSetDeliveryStatuses,
  //   setManagedDeliveryInUse: MNDLSetManagedDeliveryInUse,
  //   setManagedDeliveriesInUse: MNDLSetManagedDeliveriesInUse,
  //   setSearchedDeliveryCode: MNDLSetSearchedDeliveryCode,
  //   setSearchedDeliveryCodes: MNDLSetSearchedDeliveryCodes,
  //   setSearchedStoreCode: MNDLSetSearchedStoreCode,
  //   setSearchedStoreCodes: MNDLSetSearchedStoreCodes,
  //   setShouldFetchDeliveries: MNDLSetShouldFetchDeliveries,
  //   initManageDeliveryState: MNDLInitManageDeliveryState,
  //   fetchDeliveryDetails: MNDLFetchDeliveryDetails,
  //   initCheckedEpcs: MNDLInitCheckedEpcs,
  //   changeEpcStatus: MNDLChangeEpcStatus,
  //   checkEpcs: MNDLCheckEpcs,
  //   tab: 0
  // },
  PDM: {
    selectState: (state: RootState) => state.partialManageDeliveries,
    flowTypeRequest: 'PDM' as const,
    status: ReceivingStatus.PARTIAL,
    setDeliveryStatuses: PDMSetDeliveryStatuses,
    setManagedDeliveryInUse: PDMSetManagedDeliveryInUse,
    setManagedDeliveriesInUse: PDMSetManagedDeliveriesInUse,
    setSearchedDeliveryCode: PDMSetSearchedDeliveryCode,
    setSearchedDeliveryCodes: PDMSetSearchedDeliveryCodes,
    setSearchedStoreCode: PDMSetSearchedStoreCode,
    setSearchedStoreCodes: PDMSetSearchedStoreCodes,
    setShouldFetchDeliveries: PDMSetShouldFetchDeliveries,
    initManageDeliveryState: PDMInitManageDeliveryState,
    fetchDeliveryDetails: PDMFetchDeliveryDetails,
    initCheckedEpcs: PDMInitCheckedEpcs,
    changeEpcStatus: PDMChangeEpcStatus,
    checkEpcs: PDMCheckEpcs,
    tab: 1,
  },
};

type PdmResultModalState = {
  isVisible: boolean;
  closedDeliveries: string[];
  failedDeliveries: PdmFailedDelivery[];
};

const PageManageDeliveryDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const params = useParams<Params>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const flowType = location.state?.flowType as ManageDeliveryFlowType;
  const deliveryNumbers = useMemo(
    () => new URLSearchParams(location.search).getAll('deliveryNumber'),
    [location.search]
  );

  const manageDeliveryConfig = flowType
    ? manageDeliveryConfigByFlow[flowType]
    : manageDeliveryConfigByFlow['PDM'];

  const {
    deliveryDetails,
    checkedEpcs,
    fetchDeliveryDetailsIsLoading,
    fetchDeliveryDetailsHasError,
  } = useSelector(manageDeliveryConfig.selectState);

  // deliveryDetails is now Record<string, GetDeliveryDetailsResponse>
  const deliveryDetailsDictionary: Record<string, GetDeliveryDetailsResponse> =
    useMemo(() => deliveryDetails ?? {}, [deliveryDetails]);

  const [epcStatus, setEpcStatus] = useState<EpcStatusValues | ''>('');
  const [filterTransit, setFilterTransit] = useState<boolean>(false);
  const [fetchDetailsError, setFetchDetailsError] = useState<boolean>(false);
  const [confirmError, setConfirmError] = useState<boolean>(false);
  const [changeDeliveryStatusError, setChangeDeliveryStatusError] =
    useState<boolean>(false);
  const [confirmModalIsVisible, setConfirmModalVisibility] =
    useState<boolean>(false);
  const [stolenModalIsVisible, setStolenModalVisibility] =
    useState<boolean>(false);
  const [pdmResultModal, setPdmResultModal] = useState<PdmResultModalState>({
    isVisible: false,
    closedDeliveries: [],
    failedDeliveries: [],
  });
  const [headerExpanded, setHeaderExpanded] = useState<boolean>(false);

  useAsync(async () => {
    dispatch(manageDeliveryConfig.initCheckedEpcs());

    await dispatch(
      manageDeliveryConfig.fetchDeliveryDetails({
        deliveryNumber: deliveryNumbers,
        storeCode: params.storeCode,
      })
    );

    // This redux value is used to change the delivery status
    // in useInitApplication hook inside PageHome
    dispatch(manageDeliveryConfig.setManagedDeliveriesInUse(deliveryNumbers));
  }, []);

  // Flatten epcsInTransit and epcsOthers across all delivery items
  const allEpcsInTransit: DeliveryDetailsRow[] = useMemo(
    () =>
      Object.values(deliveryDetailsDictionary).flatMap(
        d => d.epcsInTransit ?? []
      ),
    [deliveryDetailsDictionary]
  );
  const epcsInTransit: { deliveryNumber: string; epcCode: string }[] = useMemo(
    () =>
      Object.entries(deliveryDetailsDictionary).flatMap(([deliveryNumber, d]) =>
        (d.epcsInTransit ?? []).map(e => ({
          deliveryNumber,
          epcCode: e.epcCode || '',
        }))
      ),
    [deliveryDetailsDictionary]
  );

  useEffect(() => {
    if (fetchDeliveryDetailsHasError) {
      setFetchDetailsError(true);
    }
  }, [fetchDeliveryDetailsHasError]);

  const selectValues: SelectValues = useMemo(
    () =>
      [
        { label: t('inStock'), value: EPC_STATUS.IN_STOCK },
        { label: t('missing'), value: EPC_STATUS.MISSING },
        { label: t('sold'), value: EPC_STATUS.SOLD },
        { label: t('stolen'), value: EPC_STATUS.STOLEN },
        { label: t('missingCheckOutError'), value: EPC_STATUS.CHECKOUT_ERROR },
      ].filter(
        ({ value }) => !(flowType === 'PDM' && value === EPC_STATUS.STOLEN)
      ),
    [t, flowType]
  );

  const isDeliveryStolen: boolean = useMemo(() => {
    if (allEpcsInTransit.length === 0) return false;
    return allEpcsInTransit.every(
      ({ lastEpcStatus }) => lastEpcStatus === 'Stolen'
    );
  }, [allEpcsInTransit]);

  const selectChangeHandler = (
    e: React.ChangeEvent<{ value: unknown }>
  ): void => {
    const status = e.target.value as EpcStatusValues;
    dispatch(
      manageDeliveryConfig.changeEpcStatus({
        epcs: checkedEpcs,
        status,
      })
    );
    setEpcStatus(status);
    dispatch(manageDeliveryConfig.checkEpcs([]));
  };

  const epcsInTranistLength = epcsInTransit.length;

  const checkboxClickHandler = (): void => {
    if (epcsInTransit.length === checkedEpcs.length) {
      dispatch(manageDeliveryConfig.checkEpcs([]));
    } else {
      dispatch(manageDeliveryConfig.checkEpcs(epcsInTransit));
    }
  };

  const dateHandler = (date: string): string => {
    return DateTime.fromFormat(date, 'yyyyMMdd').toFormat('yyyy LLLL dd', {
      locale: i18n.language,
    });
  };

  const confirmHandlerMNDL = async (): Promise<void> => {
    try {
      const requestBody: CloseDeliveryRequest = {
        deliveryNumber: deliveryNumbers[0],
        isDeliveryStolen,
        epcs: allEpcsInTransit.map(({ epcCode, lastEpcStatus }) => ({
          epcCode,
          epcStatuses: lastEpcStatus as EpcStatus,
        })),
      };

      await ReceiveShipmentsService.receiveshipCloseDeliveryEncoded({
        requestBody: {
          encodedRequest: btoa(JSON.stringify(requestBody)),
        },
      });

      if (stolenModalIsVisible) {
        setStolenModalVisibility(false);
      } else {
        setConfirmModalVisibility(false);
      }

      dispatch(manageDeliveryConfig.setManagedDeliveriesInUse(undefined));
      dispatch(manageDeliveryConfig.setShouldFetchDeliveries(true));

      history.push(AppRoutes.MANAGE_DELIVERY, {
        tab: manageDeliveryConfig.tab,
      });
    } catch {
      setConfirmError(true);
    }
  };

  const confirmHandlerPDM = async (): Promise<void> => {
    if (deliveryDetails) {
      try {
        const requestBody = {
          storeCode: params.storeCode,
          flowType: 'PDM',
          deliveries: Object.entries(deliveryDetails).map(
            ([deliveryNumber, delivery]) => ({
              deliveryNumber,
              epcs: (delivery.epcsInTransit ?? []).map(
                ({ epcCode, lastEpcStatus }) => ({
                  code: epcCode ?? '',
                  status: lastEpcStatus as EpcStatus,
                })
              ),
            })
          ),
        };

        const response = (await PdmService.receiveshipClosePdmDeliveryEncoded({
          requestBody: {
            encodedRequest: btoa(JSON.stringify(requestBody)),
          },
        })) as ClosePdmDeliveryResponse200 | ClosePdmDeliveryResponse207;

        setConfirmModalVisibility(false);

        const failed =
          (response as ClosePdmDeliveryResponse207).failedDeliveries ?? [];
        const closed = response.closedDeliveries ?? [];

        if (failed.length > 0) {
          // 207 — show partial result modal
          setPdmResultModal({
            isVisible: true,
            closedDeliveries: closed,
            failedDeliveries: failed,
          });
        } else {
          // 200 — all good, navigate away
          dispatch(manageDeliveryConfig.setManagedDeliveriesInUse(undefined));
          dispatch(manageDeliveryConfig.setShouldFetchDeliveries(true));
          history.push(AppRoutes.MANAGE_DELIVERY, {
            tab: manageDeliveryConfig.tab,
          });
        }
      // } catch {
      } catch (e) {
        if (e.body.failedDeliveries.length > 0) {
          setConfirmModalVisibility(false);
          // 207 — show partial result modal
          setPdmResultModal({
            isVisible: true,
            closedDeliveries: [],
            failedDeliveries: e.body.failedDeliveries,
          });
        } else {
          setConfirmError(true);
        }
      }
    }
  };

  const confirmHandler = async (): Promise<void> => {
    if (flowType === 'PDM') {
      await confirmHandlerPDM();
    } else {
      await confirmHandlerMNDL();
    }
  };

  const backHandler = async (): Promise<void> => {
    try {
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          deliveryNumbers: deliveryNumbers,
          status: manageDeliveryConfig.status,
        },
      });

      dispatch(manageDeliveryConfig.setShouldFetchDeliveries(true));
      dispatch(manageDeliveryConfig.setManagedDeliveriesInUse(undefined));

      history.push(AppRoutes.MANAGE_DELIVERY, {
        tab: manageDeliveryConfig.tab,
      });
    } catch {
      setChangeDeliveryStatusError(true);
    }
  };

  const disableConfirm = allEpcsInTransit.some(
    ({ lastEpcStatus }) => lastEpcStatus === EPC_STATUS.IN_TRANSIT
  );

  const [onGoingModalIsVisible, setOnGoingModalVisibility] =
    useState<boolean>(false);
  const [processLock, setProcessLock] = useState<ProcessStatus>();

  const checkProcessOnGoing = async (storeCode: string): Promise<boolean> => {
    const bisInProgress =
      (await ProcessesService.readProcessStatus({
        storeCode: storeCode,
        filterByStoreCode: true,
        process: 'BIS',
      })) || [];

    if (
      Array.isArray(bisInProgress) &&
      bisInProgress.length > 0 &&
      bisInProgress[0].isLocked
    ) {
      setProcessLock(bisInProgress[0]);
      setOnGoingModalVisibility(true);
      return true;
    }
    return false;
  };

  const handleConfirm = async (): Promise<void> => {
    const lockedProcess = await checkProcessOnGoing(params.storeCode);
    if (!lockedProcess) {
      isDeliveryStolen
        ? setStolenModalVisibility(true)
        : setConfirmModalVisibility(true);
    }
  };

  if (fetchDeliveryDetailsIsLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <ModalAttention
        open={stolenModalIsVisible}
        onConfirmClick={confirmHandler}
        onClose={(): void => setStolenModalVisibility(false)}
        messageMaxWidth="75%"
        message={t('stolenDeliveryMessage')}
      />
      <ModalAttention
        open={confirmModalIsVisible}
        onConfirmClick={confirmHandler}
        onClose={(): void => setConfirmModalVisibility(false)}
        messageMaxWidth="75%"
        message={t('closeDeliveryMessage')}
      />
      <UIBox width="100%" px={3} flexDirection="column">
        <StyledStickyHeader>
          {/* Per-delivery header blocks */}
          <Accordion
            expanded={headerExpanded}
            onChange={(_e, isExpanded): void => setHeaderExpanded(isExpanded)}
            style={{
              boxShadow: 'none',
              background: 'transparent',
              margin: 0,
              width: '100%',
              padding: 0,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography font="heavy">
                {t('deliveries')} (
                {Object.keys(deliveryDetailsDictionary).length}
                ):{' '}
                {Object.keys(deliveryDetailsDictionary)
                  .map(key => key)
                  .join(', ')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ padding: 0 }}>
              <UIBox flexDirection="column" width="100%">
                {Object.entries(deliveryDetailsDictionary).map(
                  ([deliveryNumber, delivery], idx) => (
                    <UIBox
                      key={deliveryNumber}
                      width="100%"
                      justifyContent="space-between"
                      mb={
                        idx < Object.keys(deliveryDetailsDictionary).length - 1
                          ? 2
                          : 0
                      }
                    >
                      <UIBox
                        width="40%"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <UIBox alignItems="center">
                          <DeliveryIcon />
                          <UIBox ml={2} flexDirection="column">
                            <Typography font="heavy">
                              #{deliveryNumber}
                            </Typography>
                            {delivery.arrivalDate && (
                              <Typography
                                size="sm"
                                color="grey"
                                margin="8px 0 0 0"
                              >
                                {dateHandler(delivery.arrivalDate)}
                              </Typography>
                            )}
                          </UIBox>
                        </UIBox>
                        {delivery.expectedQuantity && (
                          <UIBox
                            bgcolor={palette.colors.text}
                            pl={1}
                            pr={1}
                            width="max-content"
                            height="16px"
                            borderRadius="100px"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography size="sm" color="white">
                              {t('itemsFound', {
                                itemsFound: delivery.expectedQuantity,
                              })}
                            </Typography>
                          </UIBox>
                        )}
                        <UIBox alignItems="center">
                          <RoomIcon htmlColor={palette.colors.text} />
                          <Typography font="heavy" margin="0 0 0 4px">
                            {params.storeCode}
                          </Typography>
                        </UIBox>
                      </UIBox>
                    </UIBox>
                  )
                )}
              </UIBox>
            </AccordionDetails>
          </Accordion>

          <UIBox
            mt={3}
            mb={3}
            px={3}
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <UIBox width={'40%'} justifyContent="flex-end" alignItems="center">
              <Typography>{t('applyStatusChange')}</Typography>
              <StyledSelect
                marg="0 0 0 24px"
                variant="outlined"
                onBlur={(): void => setEpcStatus('')}
                value={epcStatus}
                values={selectValues}
                onChange={selectChangeHandler}
              />
            </UIBox>
            <UIBox width={'60%'} justifyContent="flex-end" alignItems="center">
              <Typography>{t('filterByStatus')}:</Typography>
              <UIBox alignItems="center">
                <UICheckbox
                  checked={filterTransit}
                  onClick={(): void => setFilterTransit(!filterTransit)}
                />
                <Typography>{t('inTransit')}</Typography>
              </UIBox>
            </UIBox>
          </UIBox>
          <StyledTablesHeader>
            <UICheckbox
              onClick={checkboxClickHandler}
              checked={
                epcsInTranistLength > 0 &&
                epcsInTranistLength === checkedEpcs.length
              }
            />
            <Typography font="heavy">UPC</Typography>
            <Typography font="heavy">EPC</Typography>
            <Typography font="heavy">{t('attributes')}</Typography>
            <Typography font="heavy">{t('lastEpcStatus')}</Typography>
          </StyledTablesHeader>
        </StyledStickyHeader>
        <StyledTableWrapper>
          {Object.entries(deliveryDetailsDictionary)
            .sort(([_, aValue], [__, bValue]) => {
              return (
                (aValue.epcsInTransit?.length ?? 0) -
                (bValue.epcsInTransit?.length ?? 0)
              );
            })
            .map(([deliveryNumber, delivery]) => {
              const visibleEpcs = filterTransit
                ? delivery.epcsInTransit ?? []
                : [
                    ...(delivery.epcsInTransit ?? []),
                    ...(delivery.epcsOthers ?? []),
                  ];

              return (
                <>
                  <DeliveryList
                    deliveryNumber={deliveryNumber}
                    delivery={delivery}
                    visibleEpcs={visibleEpcs}
                    flowType={flowType}
                  />
                </>
              );
            })}
        </StyledTableWrapper>
        <CTAContainer
          type="TWO_BUTTONS"
          onBackClick={backHandler}
          onClick={handleConfirm}
          disabledMainAction={disableConfirm}
          mainButtonLabel={t('confirm')}
        />
        <ErrorSnackbar
          open={fetchDetailsError}
          setIsOpen={setFetchDetailsError}
          errorMessage={
            fetchDeliveryDetailsHasError?.body ||
            fetchDeliveryDetailsHasError?.body?.errorMessage
          }
          ClickAwayListenerProps={{
            onClickAway: (): void => setFetchDetailsError(false),
          }}
        />
        <ErrorSnackbar
          open={changeDeliveryStatusError}
          setIsOpen={setChangeDeliveryStatusError}
          errorMessage={t('error.changeDeliveyrStatus')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setChangeDeliveryStatusError(false),
          }}
        />
        <ErrorSnackbar
          open={confirmError}
          setIsOpen={setConfirmError}
          errorMessage={t('error.confirmDeliveryEpcs')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setConfirmError(false),
          }}
        />

        <ModalDataSavedError
          iconType="ERROR"
          $minWidth="705px"
          $minHeight="260px"
          title={t('payAttention')}
          message={t('manageDeliveryDisabled', {
            process: t(processLock?.process || ''),
            lockedBy: processLock?.lockedBy || '',
            store: params.storeCode || '',
          })}
          open={onGoingModalIsVisible}
          onClose={(): void => {
            setOnGoingModalVisibility(false);
          }}
        >
          <UIBox mt={4} alignItems="center" width="50%">
            <UIButton
              outlined
              label={t('quit')}
              onClick={(): void => setOnGoingModalVisibility(false)}
            />
          </UIBox>
        </ModalDataSavedError>

        {/* PDM partial result modal (207) */}
        <PdmResultModal
          open={pdmResultModal.isVisible}
          title={t('pdm.closeResult')}
          iconType={
            pdmResultModal.closedDeliveries.length > 0 ? undefined : 'ERROR'
          }
          closedDeliveries={pdmResultModal.closedDeliveries}
          failedDeliveries={pdmResultModal.failedDeliveries}
          onClose={(): void => {
            setPdmResultModal(s => ({ ...s, isVisible: false }));
            if (
              pdmResultModal.closedDeliveries.length > 0
              ||
              pdmResultModal.failedDeliveries.length > 0
            ) {
              dispatch(
                manageDeliveryConfig.setManagedDeliveriesInUse(undefined)
              );
              dispatch(manageDeliveryConfig.setShouldFetchDeliveries(true));
              history.push(AppRoutes.MANAGE_DELIVERY, {
                tab: manageDeliveryConfig.tab,
              });
            }
          }}
        />
      </UIBox>
    </>
  );
};

export default PageManageDeliveryDetails;
